// Zuordnung Planner → Zeitplan. REINE Funktionen, kein Netz, kein DOM.
//
// Hier stehen alle Regeln, nach denen aus Planner-Buckets und -Aufgaben die
// Gruppen und Balken der Zeitleiste werden. Genau diese Regeln lebten in der
// alten App verstreut im Sync-Code; hier sind sie an EINER Stelle und testbar.

// ── Antwortformen von Graph (v1.0) ───────────────────────────────────────────
// Nur die Felder, die wir wirklich lesen. Die Formen stammen aus der
// Graph-Referenz, nicht aus Annahmen – siehe Warnung in graph/client.ts.

export type PlannerBucket = {
  id: string;
  name: string;
  orderHint: string;
};

export type PlannerTask = {
  id: string;
  title: string;
  bucketId: string;
  /** ISO-Zeitstempel oder null */
  startDateTime: string | null;
  /** ISO-Zeitstempel oder null */
  dueDateTime: string | null;
  /** Planner kennt nur 0, 50 und 100 */
  percentComplete: number;
  /** 1 = dringend, 3 = wichtig, 5 = mittel, 9 = niedrig */
  priority: number;
  /** Schlüssel = Nutzer-ID der zugewiesenen Person */
  assignments: Record<string, unknown>;
  orderHint: string;
};

// ── Unsere Formen ────────────────────────────────────────────────────────────

export type Status = "offen" | "aktiv" | "erledigt";
export type Prioritaet = "dringend" | "wichtig" | "mittel" | "niedrig";

export type Gruppe = {
  id: string;
  name: string;
  farbe: string;
  /** Die Gruppe „Termine“ wird als eine Meilenstein-Zeile gezeichnet. */
  istMeilenstein: boolean;
};

export type Aufgabe = {
  id: string;
  gruppeId: string;
  titel: string;
  /** Kalendertag JJJJ-MM-TT */
  start: string;
  /** Kalendertag JJJJ-MM-TT, bei Einzelterminen gleich `start` */
  ende: string;
  status: Status;
  prioritaet: Prioritaet;
  bearbeiterIds: string[];
};

export type Zeitplan = {
  gruppen: Gruppe[];
  aufgaben: Aufgabe[];
  /** Was NICHT gezeigt wird – und warum. Für den Hinweis in der Fußzeile. */
  uebersprungen: { backlog: number; ohneDatum: number; ohneGruppe: number };
};

// ── Regeln ───────────────────────────────────────────────────────────────────

/** Dieser Bucket ist Ideensammlung und gehört nicht auf die Zeitleiste. */
export const BACKLOG_NAME = "Backlog / Ideen";

/** Dieser Bucket wird als eine Zeile mit Rauten gezeichnet. */
export const MEILENSTEIN_NAME = "Termine";

// Planner kennt keine Bucket-Farben, also feste Zuordnung nach Namen.
// Unbekannte Namen bekommen die Ersatzfarbe.
const FARBEN: Record<string, string> = {
  Termine: "#1a5fa8",
  Geistliches: "#1a7a55",
  PR: "#b06000",
  Story: "#7c3aed",
  VLL: "#2563a8",
  Deko: "#8b1a5a",
  Verwaltung: "#c0392b",
  Allgemeines: "#6b7280",
};
const ERSATZFARBE = "#4a8070";

export function farbeFuer(bucketName: string): string {
  return FARBEN[bucketName] ?? ERSATZFARBE;
}

/**
 * Sortierung nach orderHint – die EINZIGE im Projekt, immer ABSTEIGEND.
 *
 * Das steht so nicht in der Doku, es ist empirisch gegen die Planner-Oberfläche
 * belegt – für alle drei Arten, die wir sortieren:
 *  - Buckets und Aufgaben: von der alten App im Betrieb abgeglichen.
 *  - Checklisten-Punkte: am 22.09.2026 verglichen. Planner zeigt „1. Aufgabe,
 *    2. Aufgabe, 3. usw“, aufsteigend sortiert stand es bei uns verkehrt herum.
 *
 * Die alte App sortierte die Checkliste AUFSTEIGEND – mit einer von Hand
 * nachgebauten zweiten Sortierung direkt an der Fundstelle statt über ihre
 * Hilfsfunktion. Sie zeigte Checklisten monatelang verkehrt herum, ohne dass
 * es jemandem auffiel. Deshalb gibt es hier genau eine Sortierung und keinen
 * Richtungs-Parameter: Was nur an einer Stelle steht, kann nur an einer Stelle
 * falsch sein.
 */
export function sortiereNachOrderHint<T extends { orderHint: string }>(liste: readonly T[]): T[] {
  return [...liste].sort((a, b) => (a.orderHint < b.orderHint ? 1 : a.orderHint > b.orderHint ? -1 : 0));
}

export function statusAus(percentComplete: number): Status {
  if (percentComplete >= 100) return "erledigt";
  if (percentComplete > 0) return "aktiv";
  return "offen";
}

export function prioritaetAus(priority: number | undefined): Prioritaet {
  // Planner-Oberfläche: 1 dringend, 3 wichtig, 5 mittel, 9 niedrig.
  // Alles Unbekannte (auch undefined) gilt als mittel – die Voreinstellung
  // in Planner selbst.
  if (priority === 1) return "dringend";
  if (priority === 3) return "wichtig";
  if (priority === 9) return "niedrig";
  return "mittel";
}

// ── Aufgaben-Details (werden erst beim Anklicken geladen) ────────────────────

export type PlannerChecklistItem = {
  title: string;
  isChecked: boolean;
  orderHint: string;
};

export type PlannerTaskDetails = {
  /** Die Notizen der Aufgabe. Planner liefert reinen Text, kein HTML. */
  description: string | null;
  /** Schlüssel = Kennung des Punktes. Planner liefert eine Karte, keine Liste. */
  checklist: Record<string, PlannerChecklistItem> | null;
};

export type Checkpunkt = { id: string; titel: string; erledigt: boolean };

export type AufgabenDetails = {
  notizen: string;
  checkliste: Checkpunkt[];
};

/**
 * Aus der Graph-Antwort die Details der Aufgabe.
 *
 * Planner liefert die Checkliste als KARTE (Schlüssel = Kennung), nicht als
 * Liste – die Reihenfolge einer Karte ist bedeutungslos, sie muss über
 * orderHint hergestellt werden.
 */
export function detailsAus(roh: PlannerTaskDetails): AufgabenDetails {
  const punkte = Object.entries(roh.checklist ?? {}).map(([id, p]) => ({ id, ...p }));
  return {
    notizen: (roh.description ?? "").trim(),
    checkliste: sortiereNachOrderHint(punkte).map((p) => ({
      id: p.id,
      titel: p.title,
      erledigt: p.isChecked,
    })),
  };
}

/**
 * Die nächsten Termine aus der Meilenstein-Gruppe, aufsteigend nach Datum.
 *
 * „Wann ist die Aufbauwoche?" ist die eine Frage, die man unterwegs hat. Dafür
 * lohnt der Blick aufs Handy auch dann, wenn die Zeitleiste dort nicht passt.
 * Was schon vorbei ist, bleibt weg – auf einem kleinen Schirm zählt nur, was
 * noch kommt.
 */
export function naechsteTermine(zeitplan: Zeitplan, heuteText: string, anzahl: number): Aufgabe[] {
  const gruppen = new Set(zeitplan.gruppen.filter((g) => g.istMeilenstein).map((g) => g.id));
  return zeitplan.aufgaben
    .filter((a) => gruppen.has(a.gruppeId) && a.ende >= heuteText)
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0))
    .slice(0, anzahl);
}

/** Aus einem ISO-Zeitstempel den Kalendertag JJJJ-MM-TT. */
function kalendertag(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * Baut aus den rohen Planner-Daten den Zeitplan.
 *
 * Regeln (jede einzeln getestet):
 *  1. Bucket „Backlog / Ideen“ und seine Aufgaben werden ausgeblendet.
 *  2. Bucket „Termine“ ist die Meilenstein-Gruppe und steht immer ZUERST,
 *     alle anderen folgen in Planner-Reihenfolge.
 *  3. Aufgaben ohne Start UND ohne Fälligkeit werden übersprungen.
 *  4. Fehlt nur eines der beiden Daten, übernimmt das andere: ein
 *     Einzeltermin (start === ende).
 *  5. Aufgaben in einem Bucket, den es nicht (mehr) gibt, werden übersprungen.
 *  6. Aufgaben sind nach Start aufsteigend sortiert; bei gleichem Start gilt
 *     die Planner-Reihenfolge.
 */
export function ordneZu(buckets: readonly PlannerBucket[], tasks: readonly PlannerTask[]): Zeitplan {
  const sortiert = sortiereNachOrderHint(buckets);

  const meilensteine: Gruppe[] = [];
  const gewoehnliche: Gruppe[] = [];
  const backlogIds = new Set<string>();

  for (const b of sortiert) {
    if (b.name === BACKLOG_NAME) {
      backlogIds.add(b.id);
      continue;
    }
    const gruppe: Gruppe = {
      id: b.id,
      name: b.name,
      farbe: farbeFuer(b.name),
      istMeilenstein: b.name === MEILENSTEIN_NAME,
    };
    (gruppe.istMeilenstein ? meilensteine : gewoehnliche).push(gruppe);
  }
  const gruppen = [...meilensteine, ...gewoehnliche];
  const gruppenIds = new Set(gruppen.map((g) => g.id));

  const uebersprungen = { backlog: 0, ohneDatum: 0, ohneGruppe: 0 };
  const aufgaben: Aufgabe[] = [];

  for (const t of sortiereNachOrderHint(tasks)) {
    if (backlogIds.has(t.bucketId)) {
      uebersprungen.backlog++;
      continue;
    }
    if (!gruppenIds.has(t.bucketId)) {
      uebersprungen.ohneGruppe++;
      continue;
    }
    const start = t.startDateTime ? kalendertag(t.startDateTime) : null;
    const ende = t.dueDateTime ? kalendertag(t.dueDateTime) : null;
    if (!start && !ende) {
      uebersprungen.ohneDatum++;
      continue;
    }
    aufgaben.push({
      id: t.id,
      gruppeId: t.bucketId,
      titel: t.title,
      start: (start ?? ende)!,
      ende: (ende ?? start)!,
      status: statusAus(t.percentComplete),
      prioritaet: prioritaetAus(t.priority),
      bearbeiterIds: Object.keys(t.assignments ?? {}),
    });
  }

  // Array.prototype.sort ist stabil: gleicher Start → Planner-Reihenfolge bleibt.
  aufgaben.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));

  return { gruppen, aufgaben, uebersprungen };
}
